import { Box, Divider, Flex } from "@chakra-ui/react";
import { sum } from "lodash";
import Router from "next/router";
import { useEffect, useState } from "react";
import { useRecoilValue } from "recoil";

import { WholePageLoading, WholePageProgress } from "components/Loading";
import useSyncCartHook from "components/MenuBar/CartItem/uscSyncCartHook";
import { FixedButtonsWrapper, OFButton } from "design_system/components/OFButton";
import { OFText } from "design_system/components/OFText";
import { colors } from "src/colors";
import { CURRENT_CURRENCY } from "src/constants/Common";
import { CURRENT_COUNTRY } from "src/constants/Countries";
import { URL_CHECKOUT_DELIVERY } from "src/constants/url.constants";
import { TRACKING_EVENTS_2 } from "src/dataTracking/events";
import { track$ } from "src/dataTracking/segment";
import { getAnonymousId } from "src/hooks/useTrackPageEvent";
import { isCartUpdatingState, orderSummaryState, patientState } from "src/states";
import { getTax } from "src/utils";
import { stripeCurrencyRatio } from "src/utils/envConfig";

import { EmptyCart } from "./EmptyCart";
import { CheckoutSummaryInfo } from "./OrderSummary/CheckoutSummaryInfo";
import MinimalAmountPaymentNotiSection from "./OrderSummary/MinimalAmountPaymentNotiSection";
import { useAddCartFromGuest } from "./hooks/useAddCartFromGuest";

type Props = {
  t: (key: string, values?: any) => string;
};

// THANH TODO Refactor the hook

const Checkout = ({ t }: Props) => {
  const isCartUpdating = useRecoilValue(isCartUpdatingState);
  const patient = useRecoilValue(patientState);
  const { isLoading } = useSyncCartHook(true, !isCartUpdating);
  useAddCartFromGuest();
  const [showAlertMsg, setShowAlertMsg] = useState(false);

  const { items, summary } = useRecoilValue(orderSummaryState);

  const onClickCheckout = async () => {
    const userId = patient?.user?.id;
    const ratio = stripeCurrencyRatio();
    const tax =
      sum((summary.itemList || []).map((item) => getTax(item.itemSubTotalAfterDiscount))) / ratio;
    const shipping = Number(summary.shippingFee || 0) / ratio;
    const total = Number(summary.totalNowAfterCashback) / ratio;
    const trackedData = {
      userId,
      anonymousId: !userId ? getAnonymousId() : undefined,
      event: TRACKING_EVENTS_2.checkoutStarted,
      properties: {
        total,
        tax,
        revenue: total - shipping - tax,
        shipping,
        discount: Number(summary.discountAmountNow) / ratio,
        coupon: summary.promotionCode || "",
        currency: CURRENT_CURRENCY,
        products: items.map((item) => {
          return {
            condition: Object.values(item.product?.evaluationId || {})
              .flat()
              .join(","),
            product_id: item.product?.id,
            sku: item.product?.metadata?.sku,
            name: item.product?.name,
            price: Number(item.price?.unit_amount) / ratio,
            category: item.product?.metadata?.category,
            quantity: item.price?.metadata?.dosage || item.price?.metadata?.boxes || "",
            shipping_frequency: item.price?.metadata?.frequency
          };
        }),
        platform: patient?.fromPlatformEnv
      }
    };

    track$(trackedData);
    Router.push(URL_CHECKOUT_DELIVERY);
  };

  useEffect(() => {
    if (localStorage.getItem("show_checkout_alert_msg") === "1") {
      setShowAlertMsg(true);
      localStorage.removeItem("show_checkout_alert_msg");
    }
  }, [setShowAlertMsg]);

  if (isLoading) return <WholePageLoading />;

  if (items?.length === 0) return <EmptyCart />;

  return (
    <Flex direction="column">
      {isCartUpdating && <WholePageProgress />}
      {showAlertMsg && (
        <Flex pl={[6, 0]} pr={[6, 0]}>
          <Box
            bg={colors.green[500]}
            flex={1}
            color={colors.white.default}
            fontSize={["18px", "24px"]}
            fontWeight="bold"
            pt={3}
            pb={3}
            borderRadius="6px"
            justifyContent="center"
            textAlign="center"
            alignItems="center"
            mb="20px"
          >
            <OFText textStyle="body3">{t("checkout:uncheckout-yet")}</OFText>
          </Box>
        </Flex>
      )}
      <Flex direction="column" mb="60px">
        <OFText textStyle="h3" textAlign={["left", null, "center"]}>
          {t("checkout_summary:confirm_page_title")}
        </OFText>
        <Divider m="40px 0px 12px 0px" borderColor={colors.grey[900]} />
        <CheckoutSummaryInfo summary={summary} items={items} />
        <MinimalAmountPaymentNotiSection
          t={t}
          display={
            summary?.minimalAmountPayment &&
            summary?.minimalAmountPayment === summary?.totalNowAfterCashback
          }
          minimalAmountPayment={summary?.minimalAmountPayment}
        />
      </Flex>

      <FixedButtonsWrapper mobileProps={{ zIndex: 99 }}>
        <OFButton isDisabled={items?.length === 0} onClick={onClickCheckout}>
          {t(`checkout_summary:${CURRENT_COUNTRY}_checkout_button_text`)}
        </OFButton>
      </FixedButtonsWrapper>
    </Flex>
  );
};

export default Checkout;
